import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";

import {ToastrService} from "ngx-toastr";

import {appRoutes} from "@numizmat/constants";
import {environment} from "@numizmat/environments";
import {LogoComponent} from "@numizmat/components";
import {SupportRequestApiService} from "@numizmat/services/core";
import {JsonLdService, MetaTagService} from "@numizmat/services/common";
import {InsertSupportRequestRequest} from "@numizmat/services-models/core";

@Component({
  selector: 'nz-contact-us-page',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    LogoComponent
  ],
  templateUrl: './contact-us-page.component.html',
  styleUrl: './contact-us-page.component.scss'
})
export class ContactUsPageComponent implements OnInit, OnDestroy {
  private toastrService: ToastrService = inject(ToastrService);
  private jsonLdService: JsonLdService = inject(JsonLdService);
  private metaTagService: MetaTagService = inject(MetaTagService);
  private supportRequestApiService: SupportRequestApiService = inject(SupportRequestApiService);

  protected loading: boolean = false;
  protected supportEmail = 'support@collectoria.bg';
  protected form: FormGroup = this.initializeForm();

  public ngOnInit(): void {
    this.setSchema();
    this.setMetaTags();
  }

  public ngOnDestroy(): void {
    this.jsonLdService.removeSchema();
  }

  protected onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }
    this.loading = true;
    const formValue = this.form.getRawValue();
    const request: InsertSupportRequestRequest = {
      name: formValue.name,
      email: formValue.email,
      phoneNumber: formValue.phoneNumber,
      message: formValue.message
    };

    this.supportRequestApiService.insertSupportRequest(request).subscribe({
      next: response => {
        this.form.reset();
        this.loading = false;
        this.toastrService.success("Успешно изпратихте Вашето съобщение!");
      }
    });
  }

  private initializeForm() {
    return new FormGroup({
      name: new FormControl(null, [Validators.required, Validators.maxLength(100)]),
      email: new FormControl(null, [Validators.maxLength(100)]),
      phoneNumber: new FormControl(null, [Validators.maxLength(100)]),
      message: new FormControl(null, [Validators.required, Validators.maxLength(2000)]),
    });
  }

  private setSchema(): void {
    const termsConditionsSchema = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Контакт с нас",
      "description": "Свържете се с нас на support@collectoria.bg или изпратете съобщение чрез формата за контакт.",
      "url": `${environment.applicationDomain}/${appRoutes.contactUs}`,
      "mainEntity": {
        "@type": "ContactPoint",
        "contactType": "Customer Support",
        "email": "support@collectoria.bg",
        "areaServed": "BG",
        "availableLanguage": ["Bulgarian", "English"]
      }
    };
    this.jsonLdService.setSchema(termsConditionsSchema);
  }

  private setMetaTags(): void {
    this.metaTagService.setCanonicalUrl(`${environment.applicationDomain}/${appRoutes.contactUs}`);
    this.metaTagService.setTitle('Контакт с нас | Collectoria');
    this.metaTagService.setDescription('Контакт с нас | Collectoria');
  }

  get name() {
    return this.form.get('name');
  }

  get email() {
    return this.form.get('email');
  }

  get phoneNumber() {
    return this.form.get('phoneNumber');
  }

  get message() {
    return this.form.get('message');
  }
}
